import { useEffect, useRef } from 'react';

const benefits = [
  {
    num: '01',
    title: 'More Bookings, Less Effort',
    metric: '3x',
    metricLabel: 'More Bookings',
    desc: 'Our automated booking systems capture interest 24/7 and convert visitors into confirmed appointments, with no manual follow-up needed from your team.',
  },
  {
    num: '02',
    title: 'Zero Missed Leads',
    metric: '100%',
    metricLabel: 'Lead Response Rate',
    desc: 'Every enquiry gets an instant, intelligent response. Our AI captures details, qualifies intent, and routes leads to your CRM, even at 3am.',
  },
  {
    num: '03',
    title: '24/7 Customer Support',
    metric: '24/7',
    metricLabel: 'Always On',
    desc: 'Your digital assistant never takes a day off. Handle FAQs, product questions, and booking requests around the clock without increasing headcount.',
  },
];

const BenefitsSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.benefit-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-ea-black py-24" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
            <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Key Benefits</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5">
            Results That{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
              Actually Matter
            </span>
          </h2>
          <p className="text-ea-gray text-lg max-w-2xl mx-auto">
            Real outcomes for real businesses. Measurable growth without the manual grind.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-5 sm:gap-8">
          {benefits.map((b) => (
            <div
              key={b.num}
              className="benefit-card relative bg-ea-dark-card border border-ea-dark-border rounded-xl p-5 sm:p-8 group"
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              {/* Number */}
              <p className="text-4xl sm:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-ea-blue/30 to-ea-blue/10 mb-3 sm:mb-4 leading-none">
                {b.num}
              </p>

              {/* Metric */}
              <div className="mb-3 sm:mb-4">
                <span className="text-3xl sm:text-5xl font-bold text-white">{b.metric}</span>
                <p className="text-ea-blue text-xs sm:text-sm font-medium mt-1">{b.metricLabel}</p>
              </div>

              <h3 className="text-white font-semibold text-base sm:text-xl mb-2 sm:mb-3">{b.title}</h3>
              <p className="text-ea-gray text-xs sm:text-sm leading-relaxed">{b.desc}</p>

              {/* Left border accent */}
              <div className="absolute left-0 top-6 bottom-6 w-0.5 bg-gradient-to-b from-ea-blue to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
